import React from "react";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { MobileMenuButton } from "../../../components/layout/accountMenu";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import MobileSetupCard from "./MobileSetupCard";
import MobileWeekHighlights from "./MobileWeekHighlights";
import type { WeekHighlights } from "./WelcomeCard";
import type { SetupStep } from "../setupSteps";

/**
 * What the block under the greeting stands in at while the dashboard is on its
 * way.
 *
 * One height for two different blocks — which of them belongs here is itself
 * something being loaded, so there is nothing yet to measure. It is the week's
 * depth rather than the checklist's: a workspace past its setup is the one that
 * opens this screen every day, and the checklist is a few screens in a
 * workspace's life. A setup card landing taller than the block pushes the page
 * down once; the ordinary case lands where the block stood.
 */
const FOOTPRINT = "h-[168px] rounded-card";

interface MobileWelcomeProps {
  /** The week either side of today, from the dashboard endpoint — see
   *  `WelcomeCard`, which shows the same three figures on a wide screen. */
  highlights: WeekHighlights;
  /** The workspace's setup while any of it is outstanding, `null` once there is
   *  nothing left to tick and where it could not be worked out. */
  setup?: SetupStep[] | null;
  /**
   * The offer, where there is one to make — see `UpgradeBanner`.
   *
   * It is handed in rather than worked out here because whether a workspace has
   * a plan behind it is the dashboard's fact, not the greeting's. What this
   * component owns is where it goes: under the reader's name and over whatever
   * the card below it holds, which is the one thing a phone orders differently
   * from a wide screen. There the strip sits above a card; here that would put
   * it above the screen's own title, and a page that opens on an advert reads
   * as somebody else's screen.
   */
  banner?: React.ReactNode;
  phase?: LoadPhase;
}

/**
 * What heads the dashboard on a phone: who this is, and then either what is left
 * to set up or how the week went.
 *
 * The same content as the desktop's `WelcomeCard` in a shape a phone reads
 * better. There the greeting is inside a bordered card, because on a wide screen
 * it shares a grid row with two stat tiles and has to look like one of the three
 * things in it. A phone has no such row: the greeting is the first thing on the
 * screen, under a bar that already names the app, and a border around it makes
 * the reader's own name look like one more panel of data. So the greeting sits on
 * the page itself, the way a phone app's screen is titled, and what stands under
 * it is carded instead.
 *
 * Neither of those two blocks is the desktop's row narrowed either: the setup
 * is a stack of rows that counts itself off in its own heading, and the week is
 * three cards the thumb scrolls sideways. See `MobileSetupCard` and
 * `MobileWeekHighlights`.
 *
 * The split between them is the card's, unchanged: one or the other, never
 * both — a workspace part way through its setup has no week worth reporting,
 * and a row of highlights over an unfinished checklist would be reporting a
 * week that has not happened.
 *
 * The greeting does not wait for the dashboard. It is Clerk's answer rather than
 * the endpoint's, and a phone that greets the reader while the figures are still
 * coming reads as an app opening; a screen of four grey blocks reads as one that
 * has not. Only the block below it shimmers.
 */
const MobileWelcome: React.FC<MobileWelcomeProps> = ({
  highlights,
  setup = null,
  banner = null,
  phase = "ready",
}) => {
  const { firstName, imageUrl } = useSignedInUser();

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        {/* Who is reading, beside their own name. The design puts a face at the
            head of this screen and nowhere else — it is the one place in the
            app that addresses the reader rather than naming a record, and the
            picture is part of that sentence rather than a control: what opens
            the account is the menu at the other end of this row.

            The lavender disc and the initial on it are the account menu's,
            scaled to the two lines beside it, so one account wears one face
            wherever the app draws it. 48px is that scale measured rather than
            guessed: the heading's 22px at `leading-tight` and the line under
            it come to a shade under it, so the disc stands the full height of
            the sentence it belongs to instead of hanging off its first
            line. */}
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EDE7F6]">
          {imageUrl ? (
            // Decorative: the name it belongs to is the heading right beside
            // it, and a screen reader that read both would say it twice.
            <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span
              aria-hidden
              className="text-[18px] font-semibold text-[#5B3FA8]"
            >
              {(firstName || "?").charAt(0).toUpperCase()}
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          {/* The screen's heading, and the only one it has — there is no page
              title on a phone, so this is an `h1` rather than the `h2` the
              desktop card carries under `PageHeader`.

              No name while Clerk is still restoring the session, and none for
              the account that carries neither a name nor an email. "Hello,"
              with nothing after it reads as a bug, so the comma goes with the
              name. */}
          <h1 className="text-[22px] font-semibold leading-tight tracking-tight text-ink">
            Hello{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            Welcome to your Naurist workspace
          </p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {/* No "Setup pending" pill beside the name, where the wide screen's
              card carries one: the card below now counts the steps off in its
              own heading, and a pill saying the same thing over the reader's
              name would be the screen's first word about them being that they
              are behind. See `MobileSetupCard`. */}

          {/* The way into the account menu, on the greeting's own line. The
              dashboard heads itself with the reader's name rather than with a
              title, so this is that screen's version of the row every other
              one draws through `PageHeader` — same button, same end of the
              line. */}
          <MobileMenuButton />
        </div>
      </div>

      {/* The offer, directly under the name — the strip does not wait for the
          dashboard either, so it lands with the greeting rather than after it. */}
      {banner && <div className="mt-4">{banner}</div>}

      {phase !== "ready" ? (
        <div className="mt-4">
          <SkeletonCard phase={phase} className={FOOTPRINT} />
        </div>
      ) : setup ? (
        <div className="mt-4">
          <MobileSetupCard steps={setup} />
        </div>
      ) : (
        <div className="mt-4">
          <MobileWeekHighlights highlights={highlights} />
        </div>
      )}
    </div>
  );
};

export default MobileWelcome;
